import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';
import V2LayoutPicker from '../components/V2LayoutPicker';
import { deriveAdvancedColors } from '../utils/colorDerive';
import '../styles/dashboard.css';

export default function CreateSite() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [siteName, setSiteName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [designVariables, setDesignVariables] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePickerSelect = (selectedTemplateId: string, selectedVariables: Record<string, unknown>) => {
    setTemplateId(selectedTemplateId);
    // Pass all starter variables through deriveAdvancedColors — it only fills missing keys,
    // so intentional starter values (e.g. colorAccent = indigo on a black-primary theme) are preserved.
    const withDerived = deriveAdvancedColors({ ...selectedVariables });
    setDesignVariables(withDerived);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!siteName.trim()) {
      setError('Site name is required');
      return;
    }

    if (!templateId) {
      setError('Please choose a layout and starting point');
      return;
    }

    setLoading(true);
    try {
      const site = await api.createSite(siteName.trim(), templateId);

      // Save design variables on the site content record
      if (designVariables) {
        try {
          await api.createContent(site.siteId, 'site', { designVariables });
        } catch (dvErr) {
          // Non-fatal — site was created, variables can be set later
          console.warn('Failed to save design settings:', dvErr);
        }
      }

      addToast('success', `Site "${site.siteName}" created!`);
      navigate(`/sites/${site.siteId}/setup`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>New site</h1>
      </div>

      <form onSubmit={handleSubmit} className="create-site-form">
        {error && <div className="auth-error">{error}</div>}

        <V2LayoutPicker onSelect={handlePickerSelect} />

        <div className="form-field" style={{ marginTop: 32 }}>
          <label className="field-label">Site Name</label>
          <input
            type="text"
            className="field-input"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="My Awesome Site"
            required
          />
          <span className="field-hint">
            Used to identify this site in your dashboard, and shown on the site itself
            until you set a business name in Branding.
          </span>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading || !templateId}>
            {loading ? 'Creating…' : 'Create Site'}
          </button>
        </div>
      </form>
    </div>
  );
}
